import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AccountViewListingGroupType,
  AccountViewListingStatus,
  PropertyType,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import { Box, Table, VisuallyHidden } from '@zillow/constellation-10';

import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import PropertyConstants from '../../../constants/PropertyConstants';
import PropertiesConstants from '../../../constants/PropertiesConstants';
import { useSelector } from '../../../selectors/useSelector';
import { AppSelectors, PropertiesSelectors } from '../../../selectors';
import { FixedScrollbar } from '../../../containers/ZRAMPropertiesPage/components/PropertyTable/FixedScrollbar';
import { getCurrentMonthString } from '../../../utils/dateUtils';

import { useTableObserver } from '../../../hooks/useTableObserver';

import GroupableHeaderRow from '../GroupableHeaderRow';
import GroupablePropertyRow from '../GroupablePropertyRow';

import { stickyLeftCellRecipe, TableHeaderCell } from '../GroupablePropertyRow/styles';

import type { AccountViewListingGroupDto, WebAnalyticsLeaseDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const emptyListingCount = {
  active: 0,
  archived: 0,
  atRisk: 0,
  freeBonusListingCount: 0,
  freeClaimedListingCount: 0,
  freeListingsOwnedByCustomer: 0,
  inactive: 0,
  total: 0,
};

const emptyAddress = { city: '', state: '', street: '', unit: '', zip: '' };

const SKELETON_ROW_COUNT = PropertiesConstants.PROPERTIES_LIMIT_DEFAULT;

const skeletonPlaceholderListing: AccountViewListingGroupDto = {
  type: AccountViewListingGroupType.LISTING,
  groupId: '',
  listingAlias: '',
  name: '',
  photoKey: '',
  propertyType: PropertyType.UNKNOWN,
  status: {
    status: AccountViewListingStatus.OFF_MARKET,
    tagAppearance: '',
    text: '',
  },
  isMultifamily: false,
  address: emptyAddress,
  listingCount: emptyListingCount,
};

// match listingAlias to groupId so list skeletons are not classified as multiplex unit rows (isAGroupUnitPropertyRow)
const skeletonPlaceholders: Array<AccountViewListingGroupDto> = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => {
  const id = `skeleton-${i}`;
  return {
    ...skeletonPlaceholderListing,
    groupId: id,
    listingAlias: id,
  };
});

const GroupablePropertiesViewList = ({
  listings,
  isLoading = false,
  isFetchingNextPage = false,
}: {
  listings: Array<AccountViewListingGroupDto>;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
}) => {
  const isLoadingOrFetching = isLoading || isFetchingNextPage;

  const { user, isScreenMobile, propertiesFilters } = useSelector((state) => ({
    user: state.user,
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    propertiesFilters: PropertiesSelectors.makePropertiesFilters(state),
  }));

  const filters = {
    searchText: propertiesFilters.searchText ?? '',
    subNavFilterType: propertiesFilters.subNavFilterType ?? 'all',
  };

  const tableRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [showMoreListingAlias, setShowMoreListingAlias] = useState('');
  const [hoveredCols, setHoveredCols] = useState<Record<string, boolean>>({});

  const { isScrolledToLeft, showFixedScrollbar, isTableBottomVisible } = useTableObserver({
    scrollContainerRef,
    isScreenMobile,
  });

  useEffect(() => {
    document.addEventListener('mouseup', closeOpenMenus);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeOpenMenus(e);
      }
    });

    return () => {
      document.removeEventListener('mouseup', closeOpenMenus);
      document.removeEventListener('keydown', closeOpenMenus);
    };
  }, []);

  const closeOpenMenus = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      if (tableRef.current && showMoreListingAlias && !tableRef.current.contains(e.target as Node)) {
        setTimeout(() => {
          setShowMoreListingAlias('');
        }, 0);
      }
    },
    [showMoreListingAlias],
  );

  const handleUpsellCellHover = (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => {
    if (!hoveredCols[columnLabel]) {
      PropertiesPageCSEvents.PropertyCardHover({
        user,
        currentProperty,
        lease,
        triggerObject:
          PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS[
            columnLabel as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS
          ],
      });
      setHoveredCols((prev) => ({ ...prev, [columnLabel]: true }));
    }
  };

  return (
    <Box
      data-testid="GroupablePropertiesViewListContainer"
      ref={tableRef}
      css={{
        overflow: { base: 'auto', lg: 'hidden' },
      }}
    >
      <Box
        ref={scrollContainerRef}
        css={{
          overflowX: { base: 'hidden', lg: 'auto' },
          maxWidth: '100%',
        }}
      >
        <Table.Root
          appearance="horizontal"
          aria-label="Properties list"
          size="md"
          data-testid={isLoadingOrFetching ? 'GroupablePropertiesViewListSkeleton' : 'GroupablePropertiesViewList'}
          css={{ tableLayout: 'fixed' }}
        >
          <Table.Header css={{ display: { base: 'none', lg: 'table-header-group' } }}>
            <Table.Row>
              <TableHeaderCell
                id={PROPERTY_TABLE_COLUMNS.ADDRESS}
                css={stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft })}
              >
                Address
              </TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.RENT}>
                {getCurrentMonthString(new Date())} rent
              </TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS}>Potential tenants</TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.LEASES}>Lease</TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.ACTIONS}>
                <VisuallyHidden>Actions</VisuallyHidden>
              </TableHeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {(isLoadingOrFetching ? skeletonPlaceholders : listings).map((listing, index) =>
              listing.type === AccountViewListingGroupType.MULTIPLEX ? (
                <GroupableHeaderRow
                  key={listing.groupId}
                  listing={listing}
                  showMoreListingAlias={showMoreListingAlias}
                  setShowMoreListingAlias={setShowMoreListingAlias}
                  onUpsellCellHover={handleUpsellCellHover}
                  isScrolledToLeft={isScrolledToLeft}
                  filters={filters}
                />
              ) : (
                <GroupablePropertyRow
                  key={isLoadingOrFetching ? index : listing.listingAlias}
                  index={index}
                  listing={listing}
                  isLoading={isLoadingOrFetching}
                  showMoreListingAlias={showMoreListingAlias}
                  setShowMoreListingAlias={setShowMoreListingAlias}
                  onUpsellCellHover={handleUpsellCellHover}
                  isScrolledToLeft={isScrolledToLeft}
                />
              ),
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />
    </Box>
  );
};

export { GroupablePropertiesViewList };
export default GroupablePropertiesViewList;
